import { Injectable, Inject, Optional } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, Subject, EMPTY } from 'rxjs';
import { catchError, filter, finalize, map, share, switchMap, take, tap, timeout } from 'rxjs/operators';
import { OauthService } from '@monsido/ng2/external/oauth/oauth.service';
import { tokenErrorMessages } from '../token-issue-messages.constant';
import { MonEventService } from '@monsido/ng2/services/mon-event/mon-event.service';
import { EventBusBridgeService } from '@client/app/services/event-bus-bridge.service';
import { IS_MICROFRONTEND } from '@client/mfe/tokens/mfe-tokens';
import { MfeDataService } from '@monsido/core/mfe/mfe-data.service';


const TOKEN_ROTATION_WAIT_MS = 30_000;

@Injectable({
    providedIn: 'root',
})
export class AuthInterceptor implements HttpInterceptor {

    private tokenRefreshed$: Subject<void> | null = new Subject<void>();
    private tokenRefreshInProgress$: Observable<unknown> | null = null;
    private mfeTokenRotation$: Observable<string> | null = null;
    private isTokenInvalid = false;

    constructor (
        private oauthService: OauthService,
        private monEventService: MonEventService,
        private eventBus: EventBusBridgeService,
        @Optional() @Inject(IS_MICROFRONTEND) private readonly isMicrofrontend: boolean | null,
        @Optional() private readonly mfeDataService: MfeDataService | null = null,
    ) {
        this.monEventService.addListener('scheduledRefreshTokenRequest', () => {
            if (!this.tokenRefreshInProgress$) {
                this.tokenRefreshInProgress$ = this.startRefreshToken();
                this.tokenRefreshInProgress$.subscribe();
            }
        });
    }

    intercept (request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        if (this.isTokenInvalid && !this.isOauthRequest(request.url)) {
            return this.replayRequestOnTokenRefreshed(request, next);
        }

        return next.handle(request).pipe(
            catchError((error: HttpErrorResponse) => {
                if (this.issueWithAccessToken401(error)) {
                    if (this.isMicrofrontend) {
                        if (this.oauthService.requestUsesWrongToken(request)) {
                            return next.handle(this.oauthService.updateRequestToken(request));
                        }

                        const staleToken = this.tokenFromRequest(request);
                        return this.waitForMfeTokenRotation(staleToken).pipe(
                            switchMap(() => next.handle(this.oauthService.updateRequestToken(request))),
                            catchError(() => throwError(error)),
                        );
                    }

                    if (this.isTokenInvalid) {
                        return this.replayRequestOnTokenRefreshed(request, next);
                    }

                    this.isTokenInvalid = true;
                    if (!this.tokenRefreshInProgress$) {
                        this.tokenRefreshInProgress$ = this.startRefreshToken();
                    }
                    return this.tokenRefreshInProgress$.pipe(
                        switchMap(() => next.handle(
                            this.oauthService.updateRequestToken(request),
                        )),
                    );
                }

                if (this.tokenRefreshingError(request)) {
                    this.monEventService.run('force logout');
                }
                return throwError(error);
            }),
        );
    }

    private startRefreshToken () {
        return this.oauthService.refreshAccessToken()
            .pipe(
                tap(() => this.setTokenRefreshed()),
                catchError(() => {
                    this.setTokenRefreshed();
                    // Force logout if refreshing token is failed
                    this.monEventService.run('force logout');
                    return EMPTY;
                }),
                finalize(() => {
                    this.tokenRefreshInProgress$ = null;
                }),
                share(),
            );
    }

    private replayRequestOnTokenRefreshed (request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        this.tokenRefreshed$ = new Subject<void>();
        return this.tokenRefreshed$.pipe(
            switchMap(() => {
                return next.handle(
                    this.oauthService.updateRequestToken(request),
                );
            }),
        );
    }

    private waitForMfeTokenRotation (staleToken: string): Observable<string> {
        const mfeDataService = this.mfeDataService;
        if (!mfeDataService) {
            return throwError(() => new Error('MfeDataService unavailable in MFE mode'));
        }
        if (!this.mfeTokenRotation$) {
            this.mfeTokenRotation$ = mfeDataService.config$.pipe(
                map((config) => config?.accessToken ?? ''),
                filter((token) => token !== '' && token !== staleToken),
                take(1),
                timeout(TOKEN_ROTATION_WAIT_MS),
                tap({
                    error: () => this.eventBus.requestLogout('timeout'),
                }),
                finalize(() => {
                    this.mfeTokenRotation$ = null;
                }),
                share(),
            );
        }
        return this.mfeTokenRotation$;
    }

    private tokenFromRequest (request: HttpRequest<unknown>): string {
        return (request.headers.get('Authorization') ?? '').replace(/^Bearer\s+/, '');
    }

    private isOauthRequest (url: string): boolean {
        return url.endsWith('/oauth/token');
    }

    private setTokenRefreshed (): void {
        this.isTokenInvalid = false;
        this.tokenRefreshed$?.next();
        this.tokenRefreshed$?.complete();
    }

    private issueWithAccessToken401 (error: HttpErrorResponse): boolean {
        return Boolean(error && error.status === 401 && error.error && tokenErrorMessages.includes(error.error.message));
    }

    private tokenRefreshingError (request: HttpRequest<unknown>): boolean {
        return request.params.get('grant_type') === 'refresh_token';
    }

}
